import {
  getReportsFromIndexedDB,
  storeReportOnIndexedDB,
} from '@components/Header/ReportsDrawer/ReportsDrawer.utils';
import { AnalyticsAPI } from '@core/api';
import { Reports } from '@core/models/reports/reports.models';
import { useQueries, useQuery } from '@tanstack/react-query';

const BULK_XLSX_TYPES = new Set<Reports.ReportTypes>([
  Reports.ReportTypes.TIME_SERIES_XLSX,
  Reports.ReportTypes.HISTOGRAM_XLSX,
]);

const POLL_INTERVAL_MS = 30_000;

interface UseBulkXlsxReportsOptions {
  workspaceId?: string;
  enabled?: boolean;
}

export function useBulkXlsxReports({ workspaceId, enabled = true }: UseBulkXlsxReportsOptions) {
  const storedQuery = useQuery({
    queryKey: ['bulkXlsxStoredReports'],
    queryFn: async (): Promise<Reports.ReportQueryData[]> => {
      const all = await getReportsFromIndexedDB();
      return all.filter((r) => BULK_XLSX_TYPES.has(r.type));
    },
    enabled,
    staleTime: 0,
  });

  const activeJobs = (storedQuery.data ?? []).filter(
    (r) => r.report_id != null && r.status === Reports.ReportStatus.GENERATING
  );

  const statusQueries = useQueries({
    queries: activeJobs.map((job) => ({
      queryKey: ['bulkXlsxStatus', job.report_id],
      queryFn: async (): Promise<Reports.ReportQueryData> => {
        const { data } = await AnalyticsAPI.get<Reports.ReportDTO>(`/reports/${job.report_id}`);

        const updated: Reports.ReportQueryData = {
          ...job,
          status: data.status,
          file_url: data.file_url ?? '',
          generated_at: data.generated_at ?? job.generated_at,
          is_new: job.is_new ?? false,
        };

        if (data.status !== Reports.ReportStatus.GENERATING && job.workspace_id) {
          await storeReportOnIndexedDB({ ...updated, is_new: false }, job.workspace_id);
        }

        return updated;
      },
      enabled,
      refetchOnWindowFocus: false,
      refetchInterval: (data: Reports.ReportQueryData | undefined) =>
        data?.status === Reports.ReportStatus.GENERATING ? POLL_INTERVAL_MS : false,
    })),
  });

  const polledResults = statusQueries
    .filter((q): q is typeof q & { data: Reports.ReportQueryData } => q.data != null)
    .map((q) => q.data);

  const storedReports = storedQuery.data ?? [];

  const merged = storedReports.map((stored) => {
    const polled = polledResults.find((p) => p.report_id === stored.report_id);
    return polled ?? stored;
  });

  return { reports: merged, isLoading: storedQuery.isLoading };
}
